import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';

import { FHButton, FHTextarea } from '../../../../base';
import { getUtcOffset } from '../../../../../utils/common';
import { claimsActions } from '../../../../../store';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';

import type { RootState } from '../../../../../store';
import type { ChangeEvent } from 'react';
import type { IClaimFollowUpNote } from '../../../../../utils/types';

interface IFollowUpNoteItemProps {
  note: IClaimFollowUpNote;
}

function FollowUpNoteItem({ note }: IFollowUpNoteItemProps) {
  return (
    <Box textAlign="left" mt={5}>
      <Typography color={FORTA_BLUE.primary} fontSize={14}>
        {`${moment(note.date_written).format('MMM Do - hh:mm a')} ${
          note.author ? `- ${note.author?.first_name} ${note.author?.last_name}` : ''
        }`}
      </Typography>
      <Typography>{note.follow_up_notes}</Typography>
    </Box>
  );
}

function FollowUpNotes() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: claim } = useSelector((state: RootState) => state.claims);

  const [newNote, setNewNote] = useState<string>('');

  const sortedFollowUpNotes = useMemo(() => {
    return claim?.claim_follow_up_notes
      ? [...claim.claim_follow_up_notes].sort((a, b) => {
          return moment(b.date_written).unix() - moment(a.date_written).unix();
        })
      : [];
  }, [claim?.claim_follow_up_notes]);

  const handleCreateNewNote = async () => {
    const data = {
      follow_up_notes: newNote,
      date_written: moment().toISOString(),
      utc_offset_date_written: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      claimsActions.createFollowUpNote({ getAccessTokenSilently, data, claimId: claim?.id }),
    );
    if (response?.payload?.id) {
      setNewNote('');
    }
  };

  return (
    <Box>
      <Typography textAlign="left" fontSize={21} color={FORTA_BLUE.primary} mb={4}>
        Follow-up notes
      </Typography>
      <Box>
        <Typography fontWeight={700} textAlign="left" mb={1}>
          Follow-up post
        </Typography>
        <FHTextarea
          data-testid="new-follow-up-post"
          colorTheme={Themes.Primary}
          minRows={4}
          sx={{ boxShadow: 'none', borderRadius: '8px' }}
          maxRows={8}
          placeholder="Enter follow-up notes"
          expandable={false}
          value={newNote}
          onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setNewNote(e.target.value);
          }}
          onBlur={(e) => {
            setNewNote(e.target.value);
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <FHButton
            sx={{
              backgroundColor: FORTA_WHITE,
              border: `1px solid ${FORTA_BLUE.light2}`,
              color: FORTA_GRAY4,
              '&:hover': {
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${FORTA_BLUE.light2}`,
                color: FORTA_GRAY4,
              },
            }}
            onClick={handleCreateNewNote}
          >
            Create post
          </FHButton>
        </Box>
      </Box>
      <Box>
        {sortedFollowUpNotes.map((note: IClaimFollowUpNote) => (
          <FollowUpNoteItem note={note} key={note.id} />
        ))}
      </Box>
      {claim?.date_generated ? (
        <Typography fontSize={16} fontWeight={700} color={FORTA_BLUE.primary} my={2}>
          {`Creation of claim - ${moment(claim?.date_generated).format('MM/DD/YYYY - hh:mm a')}`}
        </Typography>
      ) : null}
    </Box>
  );
}

export default FollowUpNotes;
